import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth Store - Zustand
 * Manages authentication state
 */
export const useAuthStore = create(
    persist(
        (set, get) => ({
            admin: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            // Role template permissions loaded from Access Control (not persisted — fetched fresh each session)
            // Shape: { [module]: Set<action> }
            roleTemplatePerms: null,
            // The sidebar pages the superadmin hid for this admin's role, and hid / always
            // showed for this admin (Access Control) — null until loaded, nothing hidden.
            // Kept across reloads so a hidden page never flashes up before it is read again.
            // Shape: { roleHidden: string[], userHidden: string[], userShown: string[] }
            access: null,

            // Login - stores accessToken strictly in volatile system memory
            login: (admin, token) => {
                // Clear stale branch data if switching to a different tenant
                const prevAdmin = get().admin;
                const tenantChanged = prevAdmin && String(prevAdmin.tenantId) !== String(admin.tenantId);
                if (tenantChanged || !prevAdmin) {
                    // Reset branch/tenant store so stale context from another company is not shown
                    try {
                        const branchStorage = JSON.parse(localStorage.getItem('branch-storage') || '{}');
                        if (branchStorage.state) {
                            branchStorage.state.selectedTenant = null;
                            branchStorage.state.selectedBranch = null;
                            branchStorage.state.branches = [];
                            localStorage.setItem('branch-storage', JSON.stringify(branchStorage));
                        }
                    } catch (_) {}
                }

                // A token refresh signs the same admin in again: their access stays loaded.
                const sameAdmin = prevAdmin && String(prevAdmin.id || prevAdmin._id) === String(admin?.id || admin?._id);
                set({
                    admin,
                    token,
                    isAuthenticated: true,
                    ...(sameAdmin ? {} : { roleTemplatePerms: null, access: null }),
                });
            },

            // Logout - clears in-memory state (refresh cookie is cleared on API layer)
            logout: () => {
                // Clear branch selection on logout
                try {
                    const branchStorage = JSON.parse(localStorage.getItem('branch-storage') || '{}');
                    if (branchStorage.state) {
                        branchStorage.state.selectedBranch = null;
                        branchStorage.state.branches = [];
                        localStorage.setItem('branch-storage', JSON.stringify(branchStorage));
                    }
                } catch (_) {}

                set({
                    admin: null,
                    token: null,
                    isAuthenticated: false,
                    roleTemplatePerms: null,
                    access: null,
                });
            },

            // Update admin
            updateAdmin: (admin) => {
                set({ admin });
            },

            // Load role template permissions from Access Control API
            setRoleTemplatePerms: (modules) => {
                // modules: [{ module, actions[] }]
                const perms = {};
                for (const m of (modules || [])) {
                    perms[m.module] = new Set(m.actions || []);
                }
                set({ roleTemplatePerms: perms });
            },

            clearRoleTemplatePerms: () => set({ roleTemplatePerms: null }),

            // From GET /access-control/me: { template: { sidebarHidden }, overrides: { hiddenPages, shownPages } }
            setAccess: (mine) => set({
                access: {
                    roleHidden: mine?.template?.sidebarHidden || [],
                    userHidden: mine?.overrides?.hiddenPages || [],
                    userShown: mine?.overrides?.shownPages || [],
                },
            }),

            // Check if user has permission — checks RoleTemplate first, then falls back to legacy role-based logic
            hasPermission: (module, action) => {
                const { admin, roleTemplatePerms } = get();

                if (!admin) return false;
                if (admin.role === 'superadmin') return true;

                const isReadAction = action === 'read' || action === 'view';

                // Check RoleTemplate permissions (set by Access Control page)
                if (roleTemplatePerms) {
                    const allowed = roleTemplatePerms[module];
                    if (!allowed) return false;
                    if (allowed.has(action)) return true;
                    if (isReadAction && (allowed.has('view') || allowed.has('read'))) return true;
                    return false;
                }

                // Fallback: legacy hardcoded role-based access
                if (isReadAction) {
                    const roleReadModules = {
                        tenant_admin: ['analytics', 'branches', 'sessions', 'plans', 'vouchers', 'nas', 'templates', 'devices', 'campaigns', 'leads', 'dashboard', 'tenants', 'admins'],
                        branch_admin: ['analytics', 'branches', 'sessions', 'plans', 'vouchers', 'nas', 'templates', 'devices', 'dashboard', 'admins'],
                        support: ['dashboard', 'sessions', 'devices', 'vouchers', 'plans', 'nas'],
                        viewer: ['dashboard', 'sessions', 'devices', 'plans']
                    };
                    const allowedModules = roleReadModules[admin.role] || [];
                    if (allowedModules.includes(module)) return true;
                }

                const modulePermission = admin.permissions?.find(p => p.module === module);
                if (!modulePermission || !modulePermission.actions) return false;
                if (modulePermission.actions.includes(action)) return true;
                if (isReadAction && (modulePermission.actions.includes('read') || modulePermission.actions.includes('view'))) return true;

                return false;
            },

            // Check if user has role
            hasRole: (...roles) => {
                const { admin } = get();
                return admin && roles.includes(admin.role);
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                admin: state.admin,
                isAuthenticated: state.isAuthenticated,
                access: state.access,
            }),
        }
    )
);

/**
 * Theme Store
 */
export const useThemeStore = create(
    persist(
        (set) => ({
            theme: 'light',
            toggleTheme: () =>
                set((state) => ({
                    theme: state.theme === 'light' ? 'dark' : 'light',
                })),
            setTheme: (theme) => set({ theme }),
        }),
        {
            name: 'theme-storage',
        }
    )
);

/**
 * Admin Branding Store — what this address shows: a tenant's (branch's) own admin
 * subdomain its name and logo, every other address HotWiFi's
 * (hooks/useAdminBranding.js). Kept across reloads — per address, as the browser
 * keeps storage — so its logo does not flash HotWiFi's first.
 */
export const useBrandingStore = create(
    persist(
        (set) => ({
            branding: { kind: 'platform' },
            setBranding: (branding) => set({ branding: branding || { kind: 'platform' } }),
        }),
        {
            name: 'admin-branding',
        }
    )
);

/**
 * Branch Store - Current selected branch context
 * selectedTenant is only used by superadmin — null for all other roles
 */
export const useBranchStore = create(
    persist(
        (set) => ({
            selectedTenant: null,
            selectedBranch: null,
            branches: [],

            setSelectedTenant: (tenant) => set({ selectedTenant: tenant, selectedBranch: null, branches: [] }),
            setSelectedBranch: (branch) => set({ selectedBranch: branch }),
            setBranches: (branches) => set({ branches }),
            clearBranch: () => set({ selectedBranch: null }),
            clearAll: () => set({ selectedTenant: null, selectedBranch: null, branches: [] }),
        }),
        {
            name: 'branch-storage',
        }
    )
);
